import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Detect scroll
  useEffect(() => {
    const handleScroll = () => {
      // Use a smaller threshold for scroll detection if needed
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Links for navigation
  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Restaurant', path: '/restaurant' },
    { name: 'Pêche', path: '/etangs' },
    { name: 'Tarifs', path: '/tarifs' },
    { name: 'Galerie', path: '/galerie' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`w-full z-[1000] transition-all duration-300 ease-in-out 
                  ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-primary/80 backdrop-blur-sm py-4'}`}
    >
      <div
        className="max-w-screen-xl mx-auto px-6 flex justify-between items-center"
      >
        {/* Logo */}
        <Link
          to="/"
          className={`flex items-center font-heading font-bold text-2xl no-underline transition-colors duration-300 ease-in-out 
                      ${isScrolled ? 'text-primary' : 'text-white'}`}
        >
          <img 
            src="/images/logo.png" 
            alt="Moulin Tala" 
            className="h-10 mr-2 filter drop-shadow-sm"
          />
          <span>
            Moulin Tala
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div
          className="hidden md:flex items-center space-x-8"
        >
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`relative font-body no-underline py-1 transition-colors duration-300 ease-in-out 
                            ${isScrolled ? 'text-primary' : 'text-white'} 
                            ${isActive ? 'font-bold' : 'font-normal'}`}
              >
                {link.name}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-accent rounded"
                  />
                )}
              </Link>
            );
          })}
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="block md:hidden p-1"
          aria-label="Menu"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`${isScrolled ? 'stroke-primary' : 'stroke-white'} transition-colors duration-300 ease-in-out`}
          >
            {isMenuOpen ? (
              <path
                d="M6 18L18 6M6 6L18 18"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <path
                d="M4 6H20M4 12H20M4 18H20"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div
          className={`md:hidden ${isScrolled ? 'bg-white' : 'bg-primary/90 backdrop-blur-sm'} px-6 py-4 shadow-md`}
        >
          {navLinks.map((link) => {
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 font-body no-underline border-b ${isScrolled ? 'text-primary border-gray/20' : 'text-white border-primary-light/20'} 
                            ${isActive ? 'font-bold' : 'font-normal'}`}
              >
                {link.name}
                {isActive && (
                  <span
                    className="inline-block ml-2 w-2 h-2 rounded-full bg-accent align-middle"
                  />
                )}
              </Link>
            );
          })}
        </div>
      )}
    </nav>
  );
};

export default Navbar;

// Notes for Tailwind Configuration (tailwind.config.js):
// - Extend theme colors to include: primary ('#1F4D36'), secondary ('#3A8D7B'), accent ('#F7B538'), dark ('#2B2B2B'), darkGray ('#4A5568'), gray ('#F0F2F5'), light ('#F6F8FF')
//   Example: theme: { extend: { colors: { primary: '#1F4D36', ... } } }
// - Extend theme fonts to include: heading ('Montserrat', ...), body ('Inter', ...)
//   Example: theme: { extend: { fontFamily: { heading: ['Montserrat', 'sans-serif'], body: ['Inter', 'sans-serif'] } } }
// - Consider adding a plugin for filter: drop-shadow() if needed, or define custom utility. 